/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Box } from '@mui/material';
import { getTimeString } from '../../utils/time';
import { balanceMinutes } from '../../utils/pto';

const DATE_OPTIONS = {
  weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
};

const PILL = {
  Submitted: { bg: '#fde6d2', fg: '#F19235' },
  Approved: { bg: '#eef5ea', fg: '#53A551' },
  Denied: { bg: '#fdebe6', fg: '#b94f3c' },
};

function StatusPill({ status }) {
  const c = PILL[status] || PILL.Denied;
  return (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full"
      style={{
        backgroundColor: c.bg, color: c.fg, fontSize: '11px', fontWeight: 600,
      }}
    >
      {status}
    </span>
  );
}

StatusPill.propTypes = { status: PropTypes.string.isRequired };

function Field({ label, children }) {
  return (
    <div>
      <p className="text-xs text-ink-500 font-medium mb-1">{label}</p>
      {children}
    </div>
  );
}

Field.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node.isRequired };

function ViewRequestModal({
  closeModal, request, handleDeny, handleApprove,
}) {
  const startDate = new Date(request.startDate);
  const endDate = new Date(request.endDate);
  const ptoBalance = request.member.ptoBalances[0];
  // carriedOverHours is part of the spendable balance everywhere else; this
  // screen used to leave it out and under-report the balance for anyone with
  // time carried forward from last year.
  const currentBalance = balanceMinutes(ptoBalance);
  const remainingBalance = currentBalance - request.totalHours;

  const currentBalanceStr = getTimeString(currentBalance);
  const requestedTimeStr = getTimeString(request.totalHours);
  const remainingBalanceStr = getTimeString(remainingBalance);
  const isSubmitted = request.status === 'Submitted';

  return (
    <Modal open onClose={() => closeModal(false)} className="flex-row align-c jstfy-c">
      <Box style={{
        width: 672,
        maxWidth: 'calc(100% - 32px)',
        maxHeight: '90vh',
        overflowY: 'auto',
        padding: 24,
        background: '#fff',
        borderRadius: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
      >
        <h5 className="text-lg font-semibold leading-none text-ink-900 mb-0">Time Off Request</h5>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_240px] gap-6">
          <div className="space-y-4">
            <Field label="Policy">
              <p className="text-sm text-ink-900 font-medium mb-0">{request.offTimePolicy.name}</p>
            </Field>
            <Field label="Time Span">
              <p className="text-sm text-ink-900 font-medium mb-0">
                {startDate.toLocaleDateString('en-US', DATE_OPTIONS)}
                {' '}
                to
                {' '}
                {endDate.toLocaleDateString('en-US', DATE_OPTIONS)}
              </p>
            </Field>
            <Field label="Total Time Off Hours">
              <p className="text-sm text-ink-900 font-medium tabular-nums mb-0">{requestedTimeStr}</p>
            </Field>
            <Field label="Time Off Reason">
              <p className="text-sm text-ink-900 font-medium mb-0">{request.reason}</p>
            </Field>
            <Field label="Status">
              <StatusPill status={request.status} />
            </Field>
            {request.status === 'Denied' && request.denyReason && (
              <Field label="Reason for Denial">
                <p className="text-sm text-ink-900 mb-0">{request.denyReason}</p>
              </Field>
            )}
          </div>

          <div className="border-l border-surface-1 pl-6 space-y-4">
            {isSubmitted ? (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-ink-500">Current Balance</span>
                  <span className="text-ink-900 font-medium tabular-nums">{currentBalanceStr}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-ink-500">Hours Requested</span>
                  <span className="text-ink-900 font-medium tabular-nums">{requestedTimeStr}</span>
                </div>
                <div className="border-t border-surface-1 pt-3 flex items-center justify-between text-sm">
                  <span className="text-ink-500">Remaining Balance</span>
                  <span className="text-ink-900 font-semibold tabular-nums">{remainingBalanceStr}</span>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-between text-sm">
                <span className="text-ink-500">Hours Requested</span>
                <span className="text-ink-900 font-medium tabular-nums">{requestedTimeStr}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-start items-center gap-2">
          <button
            type="button"
            onClick={() => closeModal(false)}
            className="inline-flex items-center justify-center px-4 py-2 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={request.status === 'Denied'}
            onClick={() => { closeModal(); handleDeny(); }}
            className="inline-flex items-center justify-center px-4 py-2 border border-rule rounded-[8px] text-sm font-medium text-[#b94f3c] bg-white hover:border-[#b94f3c] disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            Deny
          </button>
          <button
            type="button"
            disabled={request.status === 'Approved'}
            onClick={() => { closeModal(); handleApprove(); }}
            className="inline-flex items-center justify-center px-4 py-2 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            Approve
          </button>
        </div>
      </Box>
    </Modal>
  );
}

ViewRequestModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  handleDeny: PropTypes.func.isRequired,
  handleApprove: PropTypes.func.isRequired,
  request: PropTypes.shape({
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    totalHours: PropTypes.number.isRequired,
    offTimePolicy: PropTypes.shape({ name: PropTypes.string.isRequired }).isRequired,
    status: PropTypes.string.isRequired,
    denyReason: PropTypes.string,
    reason: PropTypes.string.isRequired,
    member: PropTypes.shape({
      // DECIMAL columns arrive from mysql2 as strings, hence oneOfType.
      ptoBalances: PropTypes.arrayOf(PropTypes.shape({
        accruedHours: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        carriedOverHours: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        usedHours: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      })),
    }),
  }).isRequired,
};

export default ViewRequestModal;
